import { useIntl } from 'react-intl';
import { useRef, useState, useCallback } from 'react';
import {
    Combobox,
    CustomTable,
    CustomTableRef,
    GetItemsProps,
    InputDate,
} from 'medere-ui-framework';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import './ProfessionalSettlementView.scss';
import { getAllCompanies } from '../../servicesHooks/Accounting/useAccountingService';
import { getProfessionals } from '../../servicesHooks/Budget/useBudgetService';
import { getClosings, getSettlementReceiptForms } from '../../services/ProfessionalSettlementController';
import { ProfessionalSettlementDTO } from '../../dto/ProfessionalSettlementDTO';
import { ComboDataDTO } from '../../dto/ComboDataDTO';
import { formatMoney } from '../../utils/money';
import { formatDate, formatDateParam, formatDateTime } from './settlementFormat';
import NewProfessionalSettlementPanel from './NewProfessionalSettlementPanel';
import SettlementDetail from './SettlementDetail';
import ReceiptFormsDialog from '../../components/ReceiptFormsDialog/ReceiptFormsDialog';
import AdditionalPaymentDialog from './AdditionalPaymentDialog';
import SettlementRowActions from './SettlementRowActions';
import AdministrativeFormStateTag from '../../components/AdministrativeFormStateTag/AdministrativeFormStateTag';
import useReceiptSummaryDownload from '../../hooks/useReceiptSummaryDownload';

type ViewType = 'history' | 'newSettlement' | 'detail';

/** Opción del combo de entidad facturadora (getAllCompanies). */
interface BillingEntityOption {
    company: number;
    description: string;
}

interface HistoryFilters {
    billingEntityId: number | null;
    professionalId: number | null;
    fromDate: string | null;
    toDate: string | null;
}

/** Rango por defecto del historial: el último mes. */
function defaultFromDate(): Date {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d;
}

function toHistoryFilters(
    company: BillingEntityOption | null,
    professional: ComboDataDTO | null,
    fromDate: Date | null,
    toDate: Date | null,
): HistoryFilters {
    return {
        billingEntityId: company?.company ?? null,
        professionalId: professional?.id ?? null,
        fromDate: fromDate ? formatDateParam(fromDate) : null,
        toDate: toDate ? formatDateParam(toDate, true) : null,
    };
}

/**
 * Saldo de la rendición, con la dirección escrita: lo que la institución todavía le debe ("A pagar") o lo que el
 * profesional debe por un pago de más que todavía no se descontó. Nunca son las dos cosas a la vez.
 */
function SettlementBalance({ settlement }: { settlement: ProfessionalSettlementDTO }) {
    if (settlement.pendingamount > 0) {
        return <span className="md-tag md-tag-warn">A pagar {formatMoney(settlement.pendingamount)}</span>;
    }
    if (settlement.overpaymentpending > 0) {
        return (
            <span
                className="md-tag md-tag-warn settlement-overpayment"
                data-pr-tooltip="Se descuenta en el próximo comprobante del profesional"
                data-pr-position="left"
            >
                Pago de más {formatMoney(settlement.overpaymentpending)}
            </span>
        );
    }
    return <>—</>;
}

function summaryFileName(settlement: ProfessionalSettlementDTO): string {
    return `resumen-rendicion-${settlement.closingnumber}.pdf`;
}

export default function ProfessionalSettlementView() {
    const intl = useIntl();
    const historyTableRef = useRef<CustomTableRef>(null);
    const summary = useReceiptSummaryDownload();

    const [currentView, setCurrentView] = useState<ViewType>('history');
    const [detailSettlement, setDetailSettlement] = useState<ProfessionalSettlementDTO | null>(null);
    const [formsSettlement, setFormsSettlement] = useState<ProfessionalSettlementDTO | null>(null);
    const [paymentSettlement, setPaymentSettlement] = useState<ProfessionalSettlementDTO | null>(null);

    // ── Filtros del historial (se aplican con "Buscar") ──
    const [historyCompany, setHistoryCompany] = useState<BillingEntityOption | null>(null);
    const [historyProfessional, setHistoryProfessional] = useState<ComboDataDTO | null>(null);
    const [historyFromDate, setHistoryFromDate] = useState<Date | null>(defaultFromDate);
    const [historyToDate, setHistoryToDate] = useState<Date | null>(() => new Date());
    const activeHistoryFiltersRef = useRef<HistoryFilters>(
        toHistoryFilters(null, null, defaultFromDate(), new Date())
    );

    const getClosingsFiltered = useCallback(
        (offset: number, pageSize: number) => getClosings(offset, pageSize, activeHistoryFiltersRef.current),
        []
    );

    function handleHistorySearch() {
        activeHistoryFiltersRef.current = toHistoryFilters(historyCompany, historyProfessional, historyFromDate, historyToDate);
        historyTableRef.current?.refreshTable?.();
    }

    function handleClearFilters() {
        const fromDate = defaultFromDate();
        const toDate = new Date();
        setHistoryCompany(null);
        setHistoryProfessional(null);
        setHistoryFromDate(fromDate);
        setHistoryToDate(toDate);
        activeHistoryFiltersRef.current = toHistoryFilters(null, null, fromDate, toDate);
        historyTableRef.current?.refreshTable?.();
    }

    function handleViewDetail(settlement: ProfessionalSettlementDTO) {
        setDetailSettlement(settlement);
        setCurrentView('detail');
    }

    function handleBackToHistory() {
        setDetailSettlement(null);
        setCurrentView('history');
    }

    function handleAdditionalPaymentRegistered() {
        setPaymentSettlement(null);
        historyTableRef.current?.refreshTable?.();
    }

    return (
        <div className="professional-settlement px-4 flex flex-column viewContainer">
            {currentView === 'history' && (
                <>
                    <div className="cc-header-row">
                        <h1 className="md-page-title">
                            {intl.formatMessage({ id: 'ProfessionalSettlement', defaultMessage: 'Rendición de profesionales' })}
                        </h1>
                        <Button
                            label="Crear"
                            icon="pi pi-plus-circle"
                            className="md-primary"
                            onClick={() => setCurrentView('newSettlement')}
                        />
                    </div>

                    <div className="professional-settlement__filters">
                        <Combobox
                            label={intl.formatMessage({ id: 'BillingEntity', defaultMessage: 'Entidad facturadora' })}
                            getItems={(p: GetItemsProps) => getAllCompanies(p)}
                            optionLabel="description"
                            value={historyCompany}
                            setValue={setHistoryCompany}
                            placeholder="Todas"
                            style={{ width: '220px' }}
                        />
                        <Combobox
                            label={intl.formatMessage({ id: 'Professional', defaultMessage: 'Profesional' })}
                            getItems={(p: GetItemsProps) => getProfessionals(p)}
                            optionLabel="name"
                            value={historyProfessional}
                            setValue={setHistoryProfessional}
                            placeholder="Todos"
                            style={{ width: '220px' }}
                        />
                        <InputDate
                            label={intl.formatMessage({ id: 'DateFrom', defaultMessage: 'Desde' })}
                            value={historyFromDate}
                            onChange={(e: { value: Date | null }) => setHistoryFromDate(e.value)}
                            style={{ width: '150px' }}
                        />
                        <InputDate
                            label={intl.formatMessage({ id: 'DateTo', defaultMessage: 'Hasta' })}
                            value={historyToDate}
                            onChange={(e: { value: Date | null }) => setHistoryToDate(e.value)}
                            style={{ width: '150px' }}
                        />
                        <Button
                            outlined
                            className="md-ghost professional-settlement__filter-action"
                            icon="pi pi-search"
                            label={intl.formatMessage({ id: 'Search', defaultMessage: 'Buscar' })}
                            onClick={handleHistorySearch}
                        />
                        <Button
                            text
                            rounded
                            icon="pi pi-eraser"
                            className="md-icon-btn professional-settlement__filter-action"
                            aria-label="Limpiar filtros"
                            tooltip="Limpiar filtros"
                            tooltipOptions={{ position: 'top' }}
                            onClick={handleClearFilters}
                        />
                    </div>

                    <Tooltip target=".settlement-overpayment" />
                    <CustomTable
                        idTable="tableProfessionalSettlements"
                        ref={historyTableRef}
                        getItems={getClosingsFiltered}
                        className="crudTable"
                        filterRow={false}
                        hideFiltersButton
                        notFoundText="No hay resultados para estos filtros."
                    >
                        <Column field="closingnumber" header="N° cierre" style={{ width: '100px' }} className="text-center" headerClassName="col-center" />
                        <Column field="closingdatetime" header={intl.formatMessage({ id: 'Date', defaultMessage: 'Fecha' })} body={(row: ProfessionalSettlementDTO) => formatDateTime(row.closingdatetime)} style={{ width: '160px' }} />
                        <Column field="billingentityname" header={intl.formatMessage({ id: 'BillingEntity', defaultMessage: 'Entidad facturadora' })} />
                        <Column field="professionalname" header={intl.formatMessage({ id: 'Professional', defaultMessage: 'Profesional' })} />
                        <Column field="fromdate" header="Desde" body={(row: ProfessionalSettlementDTO) => formatDate(row.fromdate)} style={{ width: '120px' }} />
                        <Column field="todate" header="Hasta" body={(row: ProfessionalSettlementDTO) => formatDate(row.todate)} style={{ width: '120px' }} />
                        <Column field="itemcount" header="Ítems" className="text-center" headerClassName="col-center" style={{ width: '80px' }} />
                        <Column field="totalamount" header="Total" body={(row: ProfessionalSettlementDTO) => formatMoney(row.totalamount)} className="text-right" headerClassName="col-right" style={{ width: '150px' }} />
                        <Column
                            field="professionalamount"
                            header="Pagado al profesional"
                            body={(row: ProfessionalSettlementDTO) => formatMoney(row.professionalamount)}
                            className="text-right settlement-amount"
                            headerClassName="col-right"
                            style={{ width: '170px' }}
                        />
                        <Column
                            field="pendingamount"
                            header="Saldo"
                            body={(row: ProfessionalSettlementDTO) => <SettlementBalance settlement={row} />}
                            className="text-right"
                            headerClassName="col-right"
                            style={{ width: '200px' }}
                        />
                        <Column
                            field="internalreceiptstatetype"
                            header={intl.formatMessage({ id: 'State', defaultMessage: 'Estado' })}
                            body={(row: ProfessionalSettlementDTO) => <AdministrativeFormStateTag stateType={row.internalreceiptstatetype} />}
                            style={{ width: '110px' }}
                            className="text-center"
                            headerClassName="col-center"
                        />
                        <Column
                            header=""
                            style={{ width: '64px' }}
                            className="text-center"
                            body={(row: ProfessionalSettlementDTO) => (
                                <SettlementRowActions
                                    settlement={row}
                                    onViewDetail={handleViewDetail}
                                    onViewForms={setFormsSettlement}
                                    onDownloadSummary={row => summary.download(row.internalreceiptform!, summaryFileName(row))}
                                    onRegisterAdditionalPayment={setPaymentSettlement}
                                />
                            )}
                        />
                    </CustomTable>
                </>
            )}

            {currentView === 'detail' && detailSettlement && (
                <SettlementDetail settlement={detailSettlement} onBack={handleBackToHistory} />
            )}

            {currentView === 'newSettlement' && (
                <NewProfessionalSettlementPanel
                    onClose={() => setCurrentView('history')}
                    // Al volver al historial, la tabla se monta de nuevo y trae la rendición creada
                    onSettlementCreated={() => setCurrentView('history')}
                />
            )}

            <ReceiptFormsDialog
                visible={formsSettlement != null}
                header={formsSettlement ? `Comprobantes · Cierre N° ${formsSettlement.closingnumber}` : 'Comprobantes'}
                load={() => getSettlementReceiptForms(formsSettlement!.professionalsettlement)}
                summaryFileName={formsSettlement ? summaryFileName(formsSettlement) : 'resumen-rendicion.pdf'}
                onHide={() => setFormsSettlement(null)}
            />

            <AdditionalPaymentDialog
                settlement={paymentSettlement}
                onHide={() => setPaymentSettlement(null)}
                onRegistered={handleAdditionalPaymentRegistered}
            />
        </div>
    );
}
